import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  acceptRequestApi,
  cancelRequestApi,
  createRequestApi,
  fetchMessagesApi,
  completeRequestApi,
  fetchRequestsApi,
  fetchTutorRequestsApi,
  fetchRoomsApi,
  fetchTrainingLogsApi,
  markRoomReadApi,
  sendMessageApi,
} from '~/features/matching/api/matching.api';

import type { PayloadAction } from '@reduxjs/toolkit';

import type {
  ChatMessage,
  ChatRoom,
  ClassRequest,
  ClassRequestInput,
  TrainingLog,
} from '~/features/matching/types/matching.type';

export interface MatchingState {
  requests: ClassRequest[];
  /** 지도사 콘솔 목록 — 나에게 들어온 신청. 보호자 목록과 축이 달라 따로 담는다. */
  tutorRequests: ClassRequest[];
  isLoadingTutorRequests: boolean;
  tutorRequestsRequestId?: string;
  rooms: ChatRoom[];
  /** 열려 있는 방의 메시지만 보관 — 방마다 캐시하면 무효화 시점이 불명확해져 단일 버퍼로 둔다. */
  messages: ChatMessage[];
  logs: TrainingLog[];
  isLoadingRequests: boolean;
  isLoadingRooms: boolean;
  isLoadingMessages: boolean;
  isSubmitting: boolean;
  /** 신청 완료 직후 1회 노출할 성공 신호 (페이지가 소비 후 clear) */
  lastCreatedRequestId?: string;
  /** race 가드 — 최신 요청 id 만 반영, stale 응답 폐기 (catalog.slice 와 동일 규약) */
  requestsRequestId?: string;
  roomsRequestId?: string;
  messagesRequestId?: string;
}

const initialState: MatchingState = {
  requests: [],
  tutorRequests: [],
  isLoadingTutorRequests: false,
  rooms: [],
  messages: [],
  logs: [],
  isLoadingRequests: false,
  isLoadingRooms: false,
  isLoadingMessages: false,
  isSubmitting: false,
};

export const fetchRequests = createAsyncThunk<ClassRequest[], void>(
  'matching/fetchRequests',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchRequestsApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const createRequest = createAsyncThunk<ClassRequest, ClassRequestInput>(
  'matching/createRequest',
  async (input, { rejectWithValue }) => {
    try {
      return await createRequestApi(input);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const cancelRequest = createAsyncThunk<ClassRequest, string>(
  'matching/cancelRequest',
  async (requestId, { rejectWithValue }) => {
    try {
      return await cancelRequestApi(requestId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * 지도사 수락 (보호자 뷰 범위에서는 시뮬레이션).
 * @description 신청·방을 한 액션으로 갱신해야 "수락됐는데 방이 없는" 중간 렌더가 생기지 않는다.
 */
export const acceptRequest = createAsyncThunk<
  { request: ClassRequest; room: ChatRoom },
  { requestId: string; tutorName: string; className: string }
>('matching/acceptRequest', async ({ requestId, tutorName, className }, { rejectWithValue }) => {
  try {
    return await acceptRequestApi(requestId, { tutorName, className });
  } catch (err) {
    return rejectWithValue(err);
  }
});

/** 지도사 콘솔 전용 — 나에게 들어온 신청. 보호자 목록(`requests`)과 섞이면 안 된다. */
export const fetchTutorRequests = createAsyncThunk<ClassRequest[], void>(
  'matching/fetchTutorRequests',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchTutorRequestsApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/** 수업 완료 (지도사) — 이 전이가 보호자의 후기 작성 자격을 연다. */
export const completeRequest = createAsyncThunk<ClassRequest, string>(
  'matching/completeRequest',
  async (requestId, { rejectWithValue }) => {
    try {
      return await completeRequestApi(requestId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchRooms = createAsyncThunk<ChatRoom[], void>(
  'matching/fetchRooms',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchRoomsApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchMessages = createAsyncThunk<ChatMessage[], string>(
  'matching/fetchMessages',
  async (roomId, { rejectWithValue }) => {
    try {
      return await fetchMessagesApi(roomId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const sendMessage = createAsyncThunk<ChatMessage, { roomId: string; body: string }>(
  'matching/sendMessage',
  async ({ roomId, body }, { rejectWithValue }) => {
    try {
      return await sendMessageApi(roomId, body);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const markRoomRead = createAsyncThunk<ChatRoom[], string>(
  'matching/markRoomRead',
  async (roomId, { rejectWithValue }) => {
    try {
      return await markRoomReadApi(roomId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchTrainingLogs = createAsyncThunk<TrainingLog[], string>(
  'matching/fetchTrainingLogs',
  async (requestId, { rejectWithValue }) => {
    try {
      return await fetchTrainingLogsApi(requestId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const MatchingSlice = createSlice({
  initialState,
  name: 'matching',
  reducers: {
    /** 신청 완료 신호 소비 — 성공 화면을 한 번만 띄우고 뒤로가기 재진입 시 재노출되지 않게 한다. */
    clearCreatedSignal: state => {
      state.lastCreatedRequestId = undefined;
    },
    clearMessages: state => {
      state.messages = [];
      state.messagesRequestId = undefined;
    },
    /**
     * Realtime 으로 도착한 메시지 반영.
     * @description 내가 보낸 메시지는 **전송 응답으로 이미 들어와 있고** 구독으로 한 번 더 오므로
     *  id 로 중복을 막는다. 스토어만이 "무엇이 이미 있는지"를 알기 때문에 필터링을 여기서 한다.
     *  시간순 정렬은 서버가 보장하지 않으므로(동시 삽입) createdAt 으로 다시 세운다.
     */
    messageReceived: (state, action: PayloadAction<ChatMessage>) => {
      if (state.messages.some(item => item.id === action.payload.id)) return;
      state.messages = [...state.messages, action.payload].sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchRequests.pending, (state, action) => {
        state.isLoadingRequests = true;
        state.requestsRequestId = action.meta.requestId;
      })
      .addCase(fetchRequests.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestsRequestId) return;
        state.requests = action.payload;
        state.isLoadingRequests = false;
      })
      .addCase(fetchRequests.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestsRequestId) return;
        state.isLoadingRequests = false;
      })
      .addCase(fetchTutorRequests.pending, (state, action) => {
        state.isLoadingTutorRequests = true;
        state.tutorRequestsRequestId = action.meta.requestId;
      })
      .addCase(fetchTutorRequests.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.tutorRequestsRequestId) return;
        state.tutorRequests = action.payload;
        state.isLoadingTutorRequests = false;
      })
      .addCase(completeRequest.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(completeRequest.fulfilled, (state, action) => {
        state.tutorRequests = state.tutorRequests.map(item =>
          item.id === action.payload.id ? action.payload : item,
        );
        state.isSubmitting = false;
      })
      .addCase(completeRequest.rejected, state => {
        state.isSubmitting = false;
      })
      .addCase(fetchTutorRequests.rejected, (state, action) => {
        if (action.meta.requestId !== state.tutorRequestsRequestId) return;
        state.isLoadingTutorRequests = false;
      })

      .addCase(createRequest.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(createRequest.fulfilled, (state, action) => {
        state.requests = [action.payload, ...state.requests];
        state.lastCreatedRequestId = action.payload.id;
        state.isSubmitting = false;
      })
      .addCase(createRequest.rejected, state => {
        state.isSubmitting = false;
      })

      .addCase(cancelRequest.fulfilled, (state, action) => {
        state.requests = state.requests.map(item =>
          item.id === action.payload.id ? action.payload : item,
        );
      })

      .addCase(acceptRequest.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(acceptRequest.fulfilled, (state, action) => {
        state.requests = state.requests.map(item =>
          item.id === action.payload.request.id ? action.payload.request : item,
        );
        state.rooms = [action.payload.room, ...state.rooms];
        state.isSubmitting = false;
      })
      .addCase(acceptRequest.rejected, state => {
        state.isSubmitting = false;
      })

      .addCase(fetchRooms.pending, (state, action) => {
        state.isLoadingRooms = true;
        state.roomsRequestId = action.meta.requestId;
      })
      .addCase(fetchRooms.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.roomsRequestId) return;
        state.rooms = action.payload;
        state.isLoadingRooms = false;
      })
      .addCase(fetchRooms.rejected, (state, action) => {
        if (action.meta.requestId !== state.roomsRequestId) return;
        state.isLoadingRooms = false;
      })

      .addCase(fetchMessages.pending, (state, action) => {
        state.isLoadingMessages = true;
        state.messagesRequestId = action.meta.requestId;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.messagesRequestId) return;
        state.messages = action.payload;
        state.isLoadingMessages = false;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        if (action.meta.requestId !== state.messagesRequestId) return;
        state.isLoadingMessages = false;
      })

      .addCase(sendMessage.fulfilled, (state, action) => {
        state.messages = [...state.messages, action.payload];
      })

      .addCase(markRoomRead.fulfilled, (state, action) => {
        state.rooms = action.payload;
      })

      .addCase(fetchTrainingLogs.fulfilled, (state, action) => {
        state.logs = action.payload;
      });
  },
});

export const { clearCreatedSignal, clearMessages, messageReceived } = MatchingSlice.actions;
export default MatchingSlice;
